"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { Quote } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function MemberFeedback() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: card1Ref,
    isVisible: card1Visible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: card2Ref,
    isVisible: card2Visible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: card3Ref,
    isVisible: card3Visible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: logosRef,
    isVisible: logosVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const testimonials = [{
    quote: "Code Hub transformed my hobbyist coding into professional-grade software engineering.",
    author: "Alex Rivera",
    role: "FULL STACK DEVELOPER",
    company: "Nexus Systems",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/member_alex.png",
    avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/member_alex.png-wvc-srcset",
    avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/member_alex.png-wvc-sizes"
  }, {
    quote: "The collaborative environment at Code Hub is unmatched for rapid project building.",
    author: "Sarah Chen",
    role: "SYSTEMS ARCHITECT",
    company: "Vertex Labs",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/member_sarah.webp",
    avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/member_sarah.webp-wvc-srcset",
    avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/member_sarah.webp-wvc-sizes"
  }, {
    quote: "Hardware nights at the club gave me the confidence to build my own custom server rack.",
    author: "Marcus Thorne",
    role: "HARDWARE ENGINEER",
    company: "Iron Core Tech",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/member_marcus.png",
    avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/member_marcus.png-wvc-srcset",
    avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/member_marcus.png-wvc-sizes"
  }];
  const logos = [{
    name: "Nexus",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/partner_nexus.png"
  }, {
    name: "Vertex",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/partner_vertex.png"
  }, {
    name: "Iron Core",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/partner_iron.png"
  }, {
    name: "Byte",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/partner_byte.png"
  }, {
    name: "Logic",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/partner_logic.png"
  }, {
    name: "Synth",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/partner_synth.png"
  }];
  return <section data-section-id="5139" id="member-feedback" className="relative py-24 md:py-32 lg:py-44 px-6 md:px-10 lg:px-16 bg-background text-foreground">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`grid grid-cols-12 gap-6 mb-20 md:mb-28 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="col-span-12 md:col-span-3 lg:col-span-2 md:col-start-1">
            <p className="font-default text-xs tracking-[0.2em] uppercase text-primary">MEMBER STORIES</p>
          </div>
          <h2 className="col-span-12 md:col-span-9 lg:col-span-7 md:col-start-4 lg:col-start-3 font-default text-3xl md:text-5xl lg:text-[56px] leading-[1.05] tracking-tight text-foreground">
            Building the future, one commit at a time.
          </h2>
        </div>

        {}
        <div className="relative grid grid-cols-12 gap-6">
          {}
          <div ref={card1Ref} data-index={0} className={`col-span-12 md:col-span-7 md:col-start-1 lg:col-span-5 lg:col-start-2 relative transition-all duration-1000 ease-out ${card1Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <Quote className="absolute -top-6 -left-3 w-20 h-20 -rotate-12 text-primary/20" strokeWidth={1} />
            <Card className="relative bg-card p-8 md:p-10 rounded-lg shadow-lg border border-border">
              <p className="font-default italic text-xl md:text-2xl leading-[1.4] text-foreground mb-8">
                "{testimonials[0].quote}"
              </p>
              <div className="flex items-center gap-4 pt-6 border-t border-border">
                <SafeHydrate><Avatar className="w-[60px] h-[60px] rounded-lg">
                  <AvatarImage src={testimonials[0].avatar} alt={testimonials[0].author} />
                  <AvatarFallback className="font-default text-foreground">
                    {testimonials[0].author.charAt(0)}
                  </AvatarFallback>
                </Avatar></SafeHydrate>
                <div className="flex flex-col">
                  <span className="font-default text-base text-foreground">
                    {testimonials[0].author}
                  </span>
                  <span className="font-default text-[11px] tracking-[0.15em] uppercase mt-1 text-muted-foreground">
                    {testimonials[0].role}
                  </span>
                  <span className="font-default text-[13px] text-muted-foreground mt-0.5">
                    {testimonials[0].company}
                  </span>
                </div>
              </div>
            </Card>
          </div>

          {}
          <div ref={card2Ref} data-index={1} className={`col-span-12 md:col-span-5 md:col-start-8 lg:col-span-4 lg:col-start-8 relative md:mt-20 transition-all duration-1000 ease-out delay-150 ${card2Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <Quote className="absolute -top-5 -left-2 w-16 h-16 -rotate-12 text-primary/20" strokeWidth={1} />
            <Card className="relative bg-card p-7 md:p-8 rounded-lg shadow-lg border border-border">
              <p className="font-default italic text-lg md:text-xl leading-[1.4] text-foreground mb-6">
                "{testimonials[1].quote}"
              </p>
              <div className="flex items-center gap-3 pt-5 border-t border-border">
                <SafeHydrate><Avatar className="w-12 h-12 rounded-lg">
                  <AvatarImage src={testimonials[1].avatar} alt={testimonials[1].author} />
                  <AvatarFallback className="font-default text-foreground">
                    {testimonials[1].author.charAt(0)}
                  </AvatarFallback>
                </Avatar></SafeHydrate>
                <div className="flex flex-col">
                  <span className="font-default text-sm text-foreground">
                    {testimonials[1].author}
                  </span>
                  <span className="font-default text-[10px] tracking-[0.15em] uppercase mt-0.5 text-muted-foreground">
                    {testimonials[1].role}
                  </span>
                  <span className="font-default text-xs text-muted-foreground">
                    {testimonials[1].company}
                  </span>
                </div>
              </div>
            </Card>
          </div>

          {}
          <div ref={card3Ref} data-index={2} className={`col-span-12 md:col-span-6 md:col-start-3 lg:col-span-5 lg:col-start-4 relative md:mt-16 transition-all duration-1000 ease-out delay-300 ${card3Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <Quote className="absolute -top-5 -left-2 w-16 h-16 -rotate-12 text-primary/20" strokeWidth={1} />
            <Card className="relative bg-card p-7 md:p-9 rounded-lg shadow-lg border border-border">
              <p className="font-default italic text-lg md:text-xl leading-[1.4] text-foreground mb-6">
                "{testimonials[2].quote}"
              </p>
              <div className="flex items-center gap-3 pt-5 border-t border-border">
                <SafeHydrate><Avatar className="w-12 h-12 rounded-lg">
                  <AvatarImage src={testimonials[2].avatar} alt={testimonials[2].author} />
                  <AvatarFallback className="font-default text-foreground">
                    {testimonials[2].author.charAt(0)}
                  </AvatarFallback>
                </Avatar></SafeHydrate>
                <div className="flex flex-col">
                  <span className="font-default text-sm text-foreground">
                    {testimonials[2].author}
                  </span>
                  <span className="font-default text-[10px] tracking-[0.15em] uppercase mt-0.5 text-muted-foreground">
                    {testimonials[2].role}
                  </span>
                  <span className="font-default text-xs text-muted-foreground">
                    {testimonials[2].company}
                  </span>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {}
        <div ref={logosRef} className={`mt-28 md:mt-40 text-center transition-all duration-1000 ease-out ${logosVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="font-default text-xs tracking-[0.25em] uppercase mb-10 text-muted-foreground">
            Community Partners
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-10 md:gap-x-16 gap-y-6">
            {logos.map((logo, i) => <img key={i} data-index={i} src={logo.src} alt={logo.name} loading="lazy" decoding="async" className="h-8 md:h-9 w-auto opacity-60 hover:opacity-100 transition-opacity duration-500 grayscale" />)}
          </div>
        </div>
      </div>
    </section>;
}